import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../context/ThemeContext';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const WEEKDAY_IDS = [0, 1, 2, 3, 4, 5, 6];

const BarberAvailabilitySettings = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const [barbers, setBarbers] = useState([]);
  const [selectedBarberId, setSelectedBarberId] = useState('');
  const [barberSchedule, setBarberSchedule] = useState([]);
  const [barberDayOffs, setBarberDayOffs] = useState([]);
  const [loadingBarberData, setLoadingBarberData] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [showAddDayOff, setShowAddDayOff] = useState(false);
  const [newDayOff, setNewDayOff] = useState({
    date: '',
    isFullDay: true,
    start: '12:00',
    end: '14:00',
    reason: ''
  });

  const loadBarbers = useCallback(async () => {
    try {
      const response = await axiosClient.get('/admin/barbers?pageSize=100');
      if (response.data.ok) {
        setBarbers(response.data.data);
        if (response.data.data.length > 0 && !selectedBarberId) {
          setSelectedBarberId(response.data.data[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Error loading barbers:', error);
      toast.error(t('admin.failed_to_load_barbers'));
    }
  }, [selectedBarberId, t]);

  const loadBarberSchedule = useCallback(async () => {
    if (!selectedBarberId) return;
    
    try {
      setLoadingBarberData(true);
      const response = await axiosClient.get(`/admin/barber-schedules/${selectedBarberId}/schedule`);
      setBarberSchedule(response.data);
    } catch (error) {
      console.error('Error loading barber schedule:', error);
      toast.error(t('admin.settings_loading_schedule'));
    } finally {
      setLoadingBarberData(false);
    }
  }, [selectedBarberId, t]);

  const loadBarberDayOffs = useCallback(async () => {
    if (!selectedBarberId) return;
    
    try {
      const response = await axiosClient.get(`/admin/barber-schedules/${selectedBarberId}/day-offs`);
      setBarberDayOffs(response.data);
    } catch (error) {
      console.error('Error loading barber day-offs:', error);
      toast.error(t('admin.settings_day_offs_section'));
    }
  }, [selectedBarberId, t]);

  const saveBarberSchedule = async () => {
    if (!selectedBarberId) return;
    
    try {
      setSavingSchedule(true);
      await axiosClient.put(`/admin/barber-schedules/${selectedBarberId}/schedule`, barberSchedule);
      toast.success(t('admin.settings_schedule_saved'));
    } catch (error) {
      console.error('Error saving barber schedule:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_save_schedule');
      toast.error(errorMessage);
    } finally {
      setSavingSchedule(false);
    }
  };

  const addDayOff = async () => {
    if (!selectedBarberId) return;
    
    try {
      await axiosClient.post(`/admin/barber-schedules/${selectedBarberId}/day-offs`, newDayOff);
      toast.success(t('admin.settings_day_off_barber_added'));
      setShowAddDayOff(false);
      setNewDayOff({
        date: '',
        isFullDay: true,
        start: '12:00',
        end: '14:00',
        reason: ''
      });
      loadBarberDayOffs();
    } catch (error) {
      console.error('Error adding day-off:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_add_barber_day_off_title');
      toast.error(errorMessage);
    }
  };

  const deleteDayOff = async (dayOffId) => {
    if (!selectedBarberId) return;
    
    try {
      await axiosClient.delete(`/admin/barber-schedules/${selectedBarberId}/day-offs/${dayOffId}`);
      toast.success(t('admin.settings_day_off_barber_deleted'));
      loadBarberDayOffs();
    } catch (error) {
      console.error('Error deleting day-off:', error);
      toast.error(t('admin.settings_day_off_barber_deleted'));
    }
  };

  const handleScheduleChange = (weekday, field, value) => {
    setBarberSchedule(prev => prev.map(day => 
      day.weekday === weekday 
        ? { ...day, [field]: value }
        : day
    ));
  };

  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  useEffect(() => {
    if (selectedBarberId) {
      loadBarberSchedule();
      loadBarberDayOffs();
    }
  }, [selectedBarberId, loadBarberSchedule, loadBarberDayOffs]);

  return (
    <Card>
      <CardHeader
        title={t('admin.settings_barber_avail_title')}
        subtitle={t('admin.settings_barber_avail_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        {/* Barber Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
            {t('admin.settings_select_barber')}
          </label>
          <select
            value={selectedBarberId}
            onChange={(e) => setSelectedBarberId(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
            style={{ '--accent': ACCENT }}
          >
            <option value="">{t('admin.settings_select_barber_placeholder')}</option>
            {barbers.map(barber => (
              <option key={barber.id} value={barber.id}>
                {barber.name}
              </option>
            ))}
          </select>
        </div>

        {selectedBarberId && (
          <>
            {/* Weekly Schedule Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-4">
                {t('admin.settings_weekly_schedule')}
              </h3>
              
              {loadingBarberData ? (
                <div className="flex justify-center py-8">
                  <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
                    <CircularProgress size={24} style={{ color: ACCENT }} />
                    <span>{t('admin.settings_loading_schedule')}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {WEEKDAY_IDS.map(dayId => {
                    const scheduleDay = barberSchedule.find(s => s.weekday === dayId) || {
                      weekday: dayId,
                      start: '09:00',
                      end: '18:00',
                      isEnabled: false
                    };
                    
                    return (
                      <div key={dayId} className="p-3 sm:p-4 rounded-xl bg-white/30 dark:bg-zinc-800/30 backdrop-blur-sm border border-white/20 dark:border-white/10">
                        {/* Mobile Layout */}
                        <div className="sm:hidden space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-base text-zinc-900 dark:text-zinc-100">
                              {t(`admin.settings_weekday_${dayId}`)}
                            </span>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={scheduleDay.isEnabled}
                                onChange={(e) => handleScheduleChange(dayId, 'isEnabled', e.target.checked)}
                                className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                                {t('admin.settings_enabled')}
                              </span>
                            </div>
                          </div>
                          {scheduleDay.isEnabled && (
                            <div className="flex items-center space-x-2">
                              <input
                                type="time"
                                value={scheduleDay.start}
                                onChange={(e) => handleScheduleChange(dayId, 'start', e.target.value)}
                                className="flex-1 px-3 py-2 border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                                style={{ '--accent': ACCENT }}
                              />
                              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                                {t('admin.settings_to')}
                              </span>
                              <input
                                type="time"
                                value={scheduleDay.end}
                                onChange={(e) => handleScheduleChange(dayId, 'end', e.target.value)}
                                className="flex-1 px-3 py-2 border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                                style={{ '--accent': ACCENT }}
                              />
                            </div>
                          )}
                      </div>
                      
                        {/* Desktop Layout */}
                        <div className="hidden sm:flex items-center space-x-4">
                          <div className="w-20">
                            <span className="font-medium text-zinc-900 dark:text-zinc-100">
                              {t(`admin.settings_weekday_short_${dayId}`)}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={scheduleDay.isEnabled}
                              onChange={(e) => handleScheduleChange(dayId, 'isEnabled', e.target.checked)}
                              className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">
                              {t('admin.settings_enabled')}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="time"
                              value={scheduleDay.start}
                              onChange={(e) => handleScheduleChange(dayId, 'start', e.target.value)}
                              disabled={!scheduleDay.isEnabled}
                              className={`px-2 py-1 border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${!scheduleDay.isEnabled ? 'opacity-50' : ''}`}
                              style={{ '--accent': ACCENT }}
                            />
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">
                              {t('admin.settings_to')}
                            </span>
                            <input
                              type="time"
                              value={scheduleDay.end}
                              onChange={(e) => handleScheduleChange(dayId, 'end', e.target.value)}
                              disabled={!scheduleDay.isEnabled}
                              className={`px-2 py-1 border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${!scheduleDay.isEnabled ? 'opacity-50' : ''}`}
                              style={{ '--accent': ACCENT }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          {/* Day-Offs Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                {t('admin.settings_day_offs_section')}
              </h3>
            </div>

            {/* Day-Offs List */}
            <div className="space-y-2 mb-4">
              {barberDayOffs.map(dayOff => (
                <div key={dayOff.id} className={`p-3 sm:p-4 rounded-md ${
                  isDark ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  {/* Mobile Layout */}
                  <div className="sm:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium text-sm ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {dayOff.date}
                      </span>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => deleteDayOff(dayOff.id)}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                      >
                        {t('admin.delete')}
                      </motion.button>
                    </div>
                    <div className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {dayOff.isFullDay 
                        ? t('admin.settings_full_day')
                        : `${dayOff.start} - ${dayOff.end}`
                      }
                    </div>
                    {dayOff.reason && (
                      <div className={`text-sm italic ${
                        isDark ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        {dayOff.reason}
                      </div>
                    )}
                  </div>
                  
                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div>
                      <span className={`font-medium ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {dayOff.date}
                      </span>
                      <span className={`ml-2 text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {dayOff.isFullDay 
                          ? t('admin.settings_full_day')
                          : `${dayOff.start} - ${dayOff.end}`
                        }
                      </span>
                      {dayOff.reason && (
                        <span className={`ml-2 text-sm italic ${
                          isDark ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          ({dayOff.reason})
                        </span>
                      )}
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => deleteDayOff(dayOff.id)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                    >
                      {t('admin.delete')}
                    </motion.button>
                  </div>
                </div>
              ))}
              
              {barberDayOffs.length === 0 && (
                <p className="text-sm italic text-zinc-500 dark:text-zinc-400">
                  {t('admin.settings_no_barber_day_offs')}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-white/10 dark:border-white/10 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowAddDayOff(true)}
                className="flex-1 sm:flex-initial px-4 py-2.5 text-sm rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border"
                style={{
                  background: isDark ? 'rgba(125, 102, 51, 0.1)' : 'rgba(125, 102, 51, 0.05)',
                  borderColor: isDark ? 'rgba(125, 102, 51, 0.3)' : 'rgba(125, 102, 51, 0.2)',
                  color: ACCENT,
                }}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{t('admin.settings_add_barber_day_off_title')}</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={saveBarberSchedule}
                disabled={savingSchedule}
                className="flex-1 sm:flex-initial px-6 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: ACCENT }}
              >
                {savingSchedule ? (
                  <>
                    <CircularProgress size={16} style={{ color: 'white' }} />
                    <span>{t('admin.settings_saving_schedule')}</span>
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('admin.settings_save_schedule')}</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Add Day-Off Modal */}
            {showAddDayOff && typeof document !== 'undefined' && createPortal(
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl p-4 w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
                >
                  <div className="flex items-center mb-4">
                    <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center mr-2" style={{ '--accent': ACCENT }}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {t('admin.settings_add_barber_day_off_title')}
                      </h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        {t('admin.settings_add_barber_day_off_subtitle')}
                      </p>
                    </div>
                  </div>
                    
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                        {t('admin.settings_date_label')}
                      </label>
                      <input
                        type="date"
                        value={newDayOff.date}
                        onChange={(e) => setNewDayOff(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                        style={{ '--accent': ACCENT }}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2 p-2.5 rounded-xl bg-white/50 dark:bg-zinc-800/50 border border-white/20 dark:border-white/10">
                      <input
                        type="checkbox"
                        checked={newDayOff.isFullDay}
                        onChange={(e) => setNewDayOff(prev => ({ ...prev, isFullDay: e.target.checked }))}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        {t('admin.settings_full_day')}
                      </span>
                    </div>
                    
                    {!newDayOff.isFullDay && (
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                          {t('admin.settings_time_range')}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={newDayOff.start}
                            onChange={(e) => setNewDayOff(prev => ({ ...prev, start: e.target.value }))}
                            className="flex-1 px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                            style={{ '--accent': ACCENT }}
                          />
                          <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('admin.settings_to')}</span>
                          <input
                            type="time"
                            value={newDayOff.end}
                            onChange={(e) => setNewDayOff(prev => ({ ...prev, end: e.target.value }))}
                            className="flex-1 px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                            style={{ '--accent': ACCENT }}
                          />
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                        {t('admin.settings_reason_optional')}
                      </label>
                      <input
                        type="text"
                        value={newDayOff.reason}
                        onChange={(e) => setNewDayOff(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder={t('admin.settings_reason_barber_placeholder')}
                        className="w-full px-3 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                        style={{ '--accent': ACCENT }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-4">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddDayOff(false)}
                      className="px-6 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                    >
                      {t('admin.cancel')}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={addDayOff}
                      disabled={!newDayOff.date}
                      className="px-6 py-3 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transition-all duration-200"
                      style={{ background: newDayOff.date ? ACCENT : '#9ca3af' }}
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('admin.settings_add_barber_day_off_title')}
                    </motion.button>
                  </div>
                </motion.div>
              </div>,
              document.body
            )}
          </div>
        </>
      )}
      </div>
    </Card>
  );
};

export default BarberAvailabilitySettings;
